import { Controller, useFormContext } from 'react-hook-form';
import { DateValue, parseAbsolute, parseZonedDateTime, toZoned } from '@internationalized/date';
import { IoMdTime } from 'react-icons/io';
import { TmdxDatepicker } from '@ds';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLocationDateTime } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { AirportLocationProvider } from '@lp/features/RoutePlanner/components/RouteLegForm/context/AirportLocationContext';
import { useWatchFields } from '@/hooks/useWatchFields';
import { LegLocationTypeEnum, LegTypeEnum, PredefinedPlaceType, RouteLegTypeEnum } from '@/api/apis/route';
import { CalculateGroundTimeType } from '@/api/apis/here-maps';
import { validateDateTimeValue } from '@/utils/helpers';
import { SelectItemType } from '@/types';
import { AirportLocation } from './AirportLocation';
import { SegmentLocation } from './SegmentLocation';

type DepartureLocationProps = {
  predefinedLocations: PredefinedPlaceType[];
  countriesList: SelectItemType[];
};

export const DepartureLocation = ({ predefinedLocations, countriesList }: DepartureLocationProps) => {
  const { isMainDataEdit, calculateLegTimeDuration, setLegDuration } = useLegFormContext();
  const { control, setValue, getValues } = useFormContext<CommonRouteLegSchemaValues>();
  const { departureLocation, arrivalLocation, formType, legType, isDateTimeLinked } =
    useWatchFields<CommonRouteLegSchemaValues>([
      'departureLocation',
      'arrivalLocation',
      'formType',
      'legType',
      'isDateTimeLinked',
    ] as const);

  const { handleDepartureLocationDateTime } = useLocationDateTime();

  const handleDateTimeChange = async (date: DateValue | null) => {
    if (departureLocation.timezone) {
      const dateValue = validateDateTimeValue(date);
      const zonedDateValue = dateValue ? toZoned(dateValue, departureLocation.timezone) : date;
      const dateString = dateValue && zonedDateValue ? zonedDateValue.toString() : null;

      setValue('departureDateTimeValue', zonedDateValue);
      setValue('departureDateTime', dateString || '');

      if (formType === LegTypeEnum.ground && dateString) {
        const data = await calculateLegTimeDuration(legType, CalculateGroundTimeType.DepartureTime, dateString);
        setLegDuration(formType, legType, data);
      }

      if (dateString && isDateTimeLinked && arrivalLocation.timezone) {
        const { timeDurationInMs: updatedTimeDurationInMs } = getValues();
        const dateTimeValue = parseZonedDateTime(dateString)
          .add({ milliseconds: updatedTimeDurationInMs })
          .toAbsoluteString();
        const dateTimeValueInLocationTz = parseAbsolute(dateTimeValue, arrivalLocation.timezone);

        setValue('arrivalDateTimeValue', dateTimeValueInLocationTz);
        setValue('arrivalDateTime', dateTimeValueInLocationTz.toString());
      }
    }
  };

  const isAviaForm = formType === LegTypeEnum.air;
  const isGroundForm = formType === LegTypeEnum.ground;
  const isCommercialAvia = legType === RouteLegTypeEnum.CommercialFlightAviaLeg;
  const isAirport = departureLocation.locationType === LegLocationTypeEnum.airport;
  const isCustomWithCountriesSelect =
    departureLocation.locationType === LegLocationTypeEnum.custom && countriesList.length > 1;

  const gridTemplateColumns = isAviaForm
    ? `${isCommercialAvia ? '1fr 1fr' : '10rem minmax(0, 1fr) 13rem'}`
    : `10rem ${isAirport || isCustomWithCountriesSelect ? '7rem' : ''} minmax(0, 1fr) 13rem`;

  return (
    <AirportLocationProvider locationType="departureLocation" handleDateTime={handleDepartureLocationDateTime}>
      <div
        className="grid gap-4 w-full relative z-20"
        style={{
          gridTemplateColumns,
          gridTemplateRows: '1fr',
        }}
      >
        {isGroundForm && (
          <SegmentLocation
            locationType="departureLocation"
            predefinedLocations={predefinedLocations}
            countriesList={countriesList}
            handleDateTime={handleDepartureLocationDateTime}
          />
        )}
        {isAirport && <AirportLocation />}
        <Controller
          name="departureDateTimeValue"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
            <TmdxDatepicker
              size="md"
              aria-label="requiredDepartureTime"
              label="Departure Time *"
              hideTimeZone
              hourCycle={24}
              isDisabled={!isMainDataEdit || !departureLocation.id}
              showMonthAndYearPickers={false}
              granularity="minute"
              value={value}
              onChange={handleDateTimeChange}
              isInvalid={invalid}
              {...field}
              endContent={
                <div className="pointer-events-none flex items-center">
                  <span className="text-default-400 text-small">
                    <IoMdTime size={16} />
                  </span>
                </div>
              }
            />
          )}
        />
      </div>
    </AirportLocationProvider>
  );
};
